import { useState, useRef, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useStudent } from '../../context/StudentContext';
import { XPBar } from '../ui/ProgressBar';
import { clsx } from 'clsx';

const NAV_ITEMS = [
  { to: '/dashboard',  label: 'Home',      emoji: '🏠' },
  { to: '/curriculum', label: 'Learn',     emoji: '📚' },
  { to: '/ai-tutor',   label: 'AI Tutor',  emoji: '🤖' },
  { to: '/progress',   label: 'Progress',  emoji: '⭐' },
];

export function AppLayout() {
  const { logout } = useAuth();
  const { activeChild, setActiveChild } = useStudent();
  const navigate = useNavigate();

  // Click-based dropdown state
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setMenuOpen(false);
    setActiveChild(null);
    await logout();
    navigate('/');
  };

  const handleSwitchChild = () => {
    setActiveChild(null);
    navigate('/child-select');
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa] flex flex-col">
      {/* ── Top navbar ── */}
      <header className="glass sticky top-0 z-50 border-b border-white/40">
        <div className="max-w-6xl mx-auto px-4 md:px-16 h-16 flex items-center justify-between gap-4">
          {/* Logo */}
          <NavLink to="/dashboard" className="flex items-center no-underline">
            <img src="/logo.png" alt="Tinkerbots" className="h-14 w-auto" />
          </NavLink>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {NAV_ITEMS.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => clsx(
                  'flex items-center gap-1.5 px-4 py-2 rounded-lg text-label-lg font-semibold transition-all',
                  isActive
                    ? 'bg-[#b7eaff]/40 text-[#00677f]'
                    : 'text-[#3c494e] hover:bg-[#edeeef]'
                )}
              >
                <span>{item.emoji}</span>
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Right: child avatar + XP + profile menu */}
          <div className="flex items-center gap-3">
            {/* Active child XP bar */}
            {activeChild && (
              <div className="hidden sm:block w-40">
                <XPBar
                  currentXP={activeChild.xp}
                  levelXP={1000}
                  level={activeChild.level}
                />
              </div>
            )}

            {/* Child avatar button — tap to switch */}
            {activeChild && (
              <button
                onClick={handleSwitchChild}
                title="Switch child"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#f3f4f5] border border-[#edeeef] hover:bg-[#e6f4f7] transition-colors text-label-md font-semibold text-[#3c494e]"
              >
                <span className="text-lg">{activeChild.avatarEmoji}</span>
                <span className="hidden sm:inline max-w-[80px] truncate">{activeChild.displayName}</span>
                <span className="text-[#6c797f] text-xs">↕</span>
              </button>
            )}

            {/* Parent profile / settings — click-based dropdown */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen(o => !o)}
                className={clsx(
                  'w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-label-lg transition-colors',
                  menuOpen ? 'bg-[#005566] ring-2 ring-[#00c8c8]' : 'bg-[#00677f] hover:bg-[#005566]'
                )}
                aria-label="Account menu"
                aria-expanded={menuOpen}
              >
                👤
              </button>

              {menuOpen && (
                <div className="absolute right-0 top-11 w-52 bg-white rounded-2xl shadow-xl border border-[#edeeef] py-2 z-50">
                  <NavLink
                    to="/parent"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-label-lg font-semibold text-[#3c494e] hover:bg-[#f3f4f5] rounded-lg mx-1"
                  >
                    📊 Parent dashboard
                  </NavLink>
                  <NavLink
                    to="/parent/add-child"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-label-lg font-semibold text-[#3c494e] hover:bg-[#f3f4f5] rounded-lg mx-1"
                  >
                    ➕ Add child
                  </NavLink>
                  <NavLink
                    to="/child-select"
                    onClick={() => { setMenuOpen(false); setActiveChild(null); }}
                    className="flex items-center gap-3 px-4 py-2.5 text-label-lg font-semibold text-[#3c494e] hover:bg-[#f3f4f5] rounded-lg mx-1"
                  >
                    🔄 Switch child
                  </NavLink>
                  <NavLink
                    to="/profile"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-label-lg font-semibold text-[#3c494e] hover:bg-[#f3f4f5] rounded-lg mx-1"
                  >
                    ⚙️ Account settings
                  </NavLink>
                  <div className="border-t border-[#edeeef] my-2 mx-3" />
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-label-lg font-semibold text-[#ba1a1a] hover:bg-[#fff0f0] rounded-lg mx-1 text-left"
                  >
                    🚪 Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ── Page content ── */}
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 md:px-16 py-8">
        <Outlet />
      </main>

      {/* ── Mobile bottom nav ── */}
      <nav className="md:hidden glass sticky bottom-0 border-t border-white/40">
        <div className="flex items-center justify-around h-16">
          {NAV_ITEMS.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => clsx(
                'flex flex-col items-center gap-0.5 px-3 py-2 rounded-lg transition-all',
                isActive ? 'text-[#00677f]' : 'text-[#6c797f]'
              )}
            >
              <span className="text-xl">{item.emoji}</span>
              <span className="text-label-sm font-bold">{item.label}</span>
            </NavLink>
          ))}
          {/* Mobile: switch child */}
          <button
            onClick={handleSwitchChild}
            className="flex flex-col items-center gap-0.5 px-3 py-2 text-[#6c797f]"
          >
            <span className="text-xl">{activeChild?.avatarEmoji ?? '👤'}</span>
            <span className="text-label-sm font-bold">Switch</span>
          </button>
          {/* Mobile: sign out */}
          <button
            onClick={handleLogout}
            className="flex flex-col items-center gap-0.5 px-3 py-2 text-[#ba1a1a]"
          >
            <span className="text-xl">🚪</span>
            <span className="text-label-sm font-bold">Sign out</span>
          </button>
        </div>
      </nav>
    </div>
  );
}
